import React, { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// ✅ Import Toastify styles and container
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LanguageProvider } from "./context/LanguageContext";
import { Capacitor } from "@capacitor/core";
import { SafeArea } from "capacitor-plugin-safe-area";
import type { PluginListenerHandle } from "@capacitor/core";
import AppErrorBoundary from "./components/AppErrorBoundary";

// const BODY_BOTTOM_NAV_CLASS = "has-bottom-nav";
type SafeAreaInsets = {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
};

const setupDynamicViewportHeight = () => {
  if (typeof window === "undefined") {
    return () => {};
  }

  const setAppHeight = () => {
    const viewport = window.visualViewport;
    const height = viewport?.height ?? window.innerHeight;
    document.documentElement.style.setProperty("--app-height", `${height}px`);
  };

  setAppHeight();

  const resizeHandler = () => setAppHeight();
  window.addEventListener("resize", resizeHandler);
  window.visualViewport?.addEventListener("resize", resizeHandler);

  return () => {
    window.removeEventListener("resize", resizeHandler);
    window.visualViewport?.removeEventListener("resize", resizeHandler);
  };
};

// const setupBottomNavBodyClass = () => {
//   if (typeof window === "undefined") {
//     return () => {};
//   }

//   const mediaQuery = window.matchMedia("(max-width: 767px)");
//   const applyClass = (matches: boolean) => {
//     if (matches) {
//       document.body.classList.add(BODY_BOTTOM_NAV_CLASS);
//     } else {
//       document.body.classList.remove(BODY_BOTTOM_NAV_CLASS);
//     }
//   };

//   applyClass(mediaQuery.matches);

//   const listener = (event: MediaQueryListEvent) => applyClass(event.matches);
//   mediaQuery.addEventListener("change", listener);

//   return () => {
//     mediaQuery.removeEventListener("change", listener);
//     document.body.classList.remove(BODY_BOTTOM_NAV_CLASS);
//   };
// };

const setupProductionInteractionGuards = () => {
  if (typeof window === "undefined" || !import.meta.env.PROD) {
    return () => {};
  }

  const handleContextMenu = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    const isEditableTarget =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target?.isContentEditable;

    if (isEditableTarget) {
      return;
    }

    event.preventDefault();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    const blockedShortcut =
      key === "f12" ||
      (event.ctrlKey && event.shiftKey && (key === "i" || key === "j")) ||
      (event.ctrlKey && key === "u");

    if (blockedShortcut) {
      event.preventDefault();
    }
  };

  document.addEventListener("contextmenu", handleContextMenu);
  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("contextmenu", handleContextMenu);
    document.removeEventListener("keydown", handleKeyDown);
  };
};

let teardownViewportHeight: (() => void) | undefined;
// let teardownBodyClass: (() => void) | undefined;
let teardownSafeArea: (() => void) | undefined;
let teardownInteractionGuards: (() => void) | undefined;

const setupSafeAreaInsets = () => {
  if (typeof window === "undefined" || !Capacitor.isNativePlatform()) {
    return () => {};
  }

  const applyInsets = (insets: SafeAreaInsets) => {
    const root = document.documentElement;
    if (!root) return;

    if (typeof insets.top === "number") {
      root.style.setProperty("--top-safe-area", `${insets.top}px`);
      root.style.setProperty("--safe-area-inset-top", `${insets.top}px`);
    }
    if (typeof insets.bottom === "number") {
      root.style.setProperty("--bottom-safe-area", `${insets.bottom}px`);
      root.style.setProperty("--safe-area-inset-bottom", `${insets.bottom}px`);
    }
    if (typeof insets.left === "number") {
      root.style.setProperty("--safe-area-inset-left", `${insets.left}px`);
    }
    if (typeof insets.right === "number") {
      root.style.setProperty("--safe-area-inset-right", `${insets.right}px`);
    }
  };

  const syncInsets = async () => {
    try {
      const { insets } = await SafeArea.getSafeAreaInsets();
      applyInsets(insets);
    } catch (error) {
      console.warn("SafeArea.getSafeAreaInsets failed", error);
    }
  };

  syncInsets();

  let listenerHandle: PluginListenerHandle | undefined;
  SafeArea.addListener("safeAreaChanged", ({ insets }) => applyInsets(insets))
    .then((handle) => {
      listenerHandle = handle;
    })
    .catch((error) => {
      console.warn("SafeArea listener failed", error);
    });

  return () => listenerHandle?.remove();
};

if (typeof window !== "undefined") {
  teardownViewportHeight = setupDynamicViewportHeight();
  // teardownBodyClass = setupBottomNavBodyClass();
  teardownSafeArea = setupSafeAreaInsets();
  teardownInteractionGuards = setupProductionInteractionGuards();

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      teardownViewportHeight?.();
      // teardownBodyClass?.();
      teardownSafeArea?.();
      teardownInteractionGuards?.();
    });
  }
}

const rootElement = document.getElementById("root");

if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <AppErrorBoundary>
        <LanguageProvider>
          <App />

          {/* ✅ ToastContainer must be inside React tree */}
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </LanguageProvider>
      </AppErrorBoundary>
    </StrictMode>,
  );
} else {
  console.error("Root element not found");
}
